import {useCallback, useMemo} from 'react';

import {useDispatch, usePropsSelector} from '../../../../state/hooks';
import {updateConfigs} from '../../../../state/views/panel/actions';
import {
  makeSectionSettingOverridesSelector,
  makeWorkspaceSettingOverridesSelector,
} from '../../../../state/views/workspaceSettings/selectors';
import {
  getSettingOverrideCounts,
  SettingOverrideKey,
} from '../../../../state/views/workspaceSettings/settingOverrideUtils';
import {useWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {useSectionSettingsContext} from '../SectionSettingsContextProvider';
import {LinePlotSettings, SettingLevel} from '../types';
import {useWorkspaceSettingsContext} from '../WorkspaceSettingsContextProvider';

export type ClearSettingFnType = (
  settingLevel: SettingLevel,
  settingKey: SettingOverrideKey,
  defaults: Partial<LinePlotSettings>
) => void;

export const useWorkspaceSettingOverrides = () => {
  const {updateLinePlotWorkspaceSettings, updateAllLinePlotSectionSettings} =
    useWorkspaceSettingsContext();

  const {panelBankConfigRef, workspaceSettingsRef} = useWorkspaceRefsContext();
  const {panelOverrides, sectionOverrides} = usePropsSelector(
    makeWorkspaceSettingOverridesSelector,
    workspaceSettingsRef,
    panelBankConfigRef
  );

  const dispatch = useDispatch();
  const clearSetting: ClearSettingFnType = useCallback(
    (
      settingLevel: SettingLevel,
      settingKey: SettingOverrideKey,
      defaults: Partial<LinePlotSettings>
    ) => {
      if (settingLevel === 'workspace') {
        return updateLinePlotWorkspaceSettings(defaults);
      }
      if (settingLevel === 'section') {
        return updateAllLinePlotSectionSettings(defaults);
      }
      return dispatch(
        updateConfigs(
          panelOverrides[settingKey].map(p => p.ref),
          defaults
        )
      );
    },
    [
      dispatch,
      panelOverrides,
      updateAllLinePlotSectionSettings,
      updateLinePlotWorkspaceSettings,
    ]
  );

  // TODO - clear multiple settings at once

  const settingOverrideCounts = useMemo(() => {
    return {
      panels: getSettingOverrideCounts(panelOverrides),
      sections: getSettingOverrideCounts(sectionOverrides),
    };
  }, [sectionOverrides, panelOverrides]);

  return {
    clearSetting,
    settingOverrideCounts,
    sectionOverrides,
    panelOverrides,
  };
};

export const useSectionSettingOverrides = () => {
  const {updateLinePlotSectionSettings, sectionRef} =
    useSectionSettingsContext();
  const {panelOverrides} = usePropsSelector(
    makeSectionSettingOverridesSelector,
    sectionRef
  );

  const dispatch = useDispatch();
  const clearSetting: ClearSettingFnType = useCallback(
    (settingLevel, settingKey, defaults) => {
      if (settingLevel === 'section') {
        return updateLinePlotSectionSettings(defaults);
      }
      return dispatch(
        updateConfigs(
          panelOverrides[settingKey].map(p => p.ref),
          defaults
        )
      );
    },
    [dispatch, panelOverrides, updateLinePlotSectionSettings]
  );

  const settingOverrideCounts = useMemo(() => {
    return {
      panels: getSettingOverrideCounts(panelOverrides),
    };
  }, [panelOverrides]);

  return {clearSetting, panelOverrides, settingOverrideCounts};
};
